import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
axios.defaults.withCredentials = true;
import {
  Mail, Lock, Eye, EyeOff, LogOut, Plus, ArrowLeft,
  Globe, Video, Megaphone, Clock, MessageCircle, Send,
  Paperclip, Loader2, RefreshCw, Users, Mic, Square, Bell, Archive, Briefcase, Sun, Moon
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_API_URL || "http://localhost:8888/crm_corona/api_assistenza";
const API = BACKEND_URL;
const APP_VERSION = "2.0.6";

const THEME_STORAGE_KEY = "app_assistenza_theme";
const DEFAULT_APP_THEME = "dark";

const normalizeAppTheme = (value) => {
  return value === "light" || value === "dark" ? value : DEFAULT_APP_THEME;
};

const getInitialAppTheme = () => {
  if (typeof window === "undefined") {
    return DEFAULT_APP_THEME;
  }

  try {
    return normalizeAppTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch (_) {
    return DEFAULT_APP_THEME;
  }
};

const applyAppTheme = (theme) => {
  if (typeof document === "undefined") return;

  const normalizedTheme = normalizeAppTheme(theme);

  document.documentElement.dataset.theme = normalizedTheme;

  if (document.body) {
    document.body.dataset.theme = normalizedTheme;
  }
};

applyAppTheme(getInitialAppTheme());

const CLOUDINARY_CLOUD_NAME = "AppAssistenza";
const CLOUDINARY_VIDEO_UPLOAD_PRESET = "Preset_video";

const isStandalonePwa = () => {
  if (typeof window === "undefined") return false;

  const iosStandalone = window.navigator.standalone === true;
  const mediaStandalone = typeof window.matchMedia === "function"
    ? window.matchMedia("(display-mode: standalone)").matches
    : false;

  return iosStandalone || mediaStandalone;
};

const isIosDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;

  const ua = window.navigator.userAgent || "";
  const platform = window.navigator.platform || "";

  return /iPad|iPhone|iPod/.test(ua) ||
    (platform === "MacIntel" && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
};

const canUsePushNotifications = () => {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
};

const canRequestNotifications = () => {
  return canUsePushNotifications();
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const getPushPublicKey = async () => {
  const res = await axios.get(`${API}/push_public_key.php`);
  const key = String(res?.data?.publicKey || "").trim();

  if (!res?.data?.success || !key) {
    throw new Error("Public key push non disponibile");
  }

  return key;
};

const registerPushSubscription = async () => {
  if (!canUsePushNotifications()) {
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  const existingSubscription = await registration.pushManager.getSubscription();

  if (existingSubscription) {
    await axios.post(`${API}/save_push_subscription.php`, {
      subscription: existingSubscription.toJSON()
    });
    return existingSubscription;
  }

  const publicKey = await getPushPublicKey();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey)
  });

  await axios.post(`${API}/save_push_subscription.php`, {
    subscription: subscription.toJSON()
  });

  return subscription;
};

const syncPushSubscriptionIfGranted = async () => {
  if (!canUsePushNotifications()) {
    return null;
  }

  if (Notification.permission !== "granted") {
    return null;
  }

  return registerPushSubscription();
};

const setAppBadgeCount = async (count) => {
  const badgeCount = Math.max(0, Number(count || 0));

  try {
    if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
      return;
    }

    if (badgeCount > 0 && "setAppBadge" in navigator) {
      await navigator.setAppBadge(badgeCount);
    } else if ("clearAppBadge" in navigator) {
      await navigator.clearAppBadge();
    }
  } catch (_) {}

  try {
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: badgeCount > 0 ? "SET_BADGE" : "CLEAR_BADGE",
        count: badgeCount
      });
    }
  } catch (_) {}
};

const clearAppBadgeCount = async () => {
  await setAppBadgeCount(0);
};

const useServiceWorkerUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const refreshingRef = useRef(false);

  const trackInstallingWorker = useCallback((worker) => {
    if (!worker) return;

    worker.addEventListener("statechange", () => {
      if (
        worker.state === "installed" &&
        navigator.serviceWorker &&
        navigator.serviceWorker.controller
      ) {
        setWaitingWorker(worker);
        setUpdateAvailable(true);
      }
    });
  }, []);

  const bindRegistration = useCallback((registration) => {
    if (!registration) return;

    if (registration.waiting) {
      setWaitingWorker(registration.waiting);
      setUpdateAvailable(true);
    }

    if (registration.installing) {
      trackInstallingWorker(registration.installing);
    }

    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      trackInstallingWorker(newWorker);
    });
  }, [trackInstallingWorker]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    let mounted = true;

    const onControllerChange = () => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    const checkForUpdate = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration || !mounted) return;

        bindRegistration(registration);

        try {
          await registration.update();
        } catch (_) {}
      } catch (_) {}
    };

    checkForUpdate();

    const visibilityHandler = async () => {
      if (document.visibilityState === "visible") {
        await checkForUpdate();
      }
    };

    document.addEventListener("visibilitychange", visibilityHandler);

    const interval = setInterval(checkForUpdate, 60000);

    return () => {
      mounted = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", visibilityHandler);
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, [bindRegistration]);

  const applyUpdate = useCallback(() => {
    if (!waitingWorker) return;
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  }, [waitingWorker]);

  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  return {
    updateAvailable,
    applyUpdate,
    dismissUpdate
  };
};

const getAttachmentUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://unicosuite.cloud${url}`;
};

const isVideoFile = (file) => {
  if (!file) return false;

  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";

  if (type.startsWith("video/")) return true;
  return ["mp4", "mov", "webm"].includes(ext);
};

const getChatStatusLabel = (status) => {
  return statusLabels[status] || "Archiviata";
};

const getChatStatusColor = (status) => {
  return statusColors[status] || "var(--status-completata)";
};

const formatChatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const getMessageDayKey = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const capitalizeFirstLetter = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatMessageDayLabel = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const messageStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((todayStart - messageStart) / 86400000);

  if (diffDays === 0) return "Oggi";
  if (diffDays === 1) return "Ieri";

  const sameYear = today.getFullYear() === date.getFullYear();

  const label = date.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" })
  });

  return capitalizeFirstLetter(label);
};

const ChatDateSeparator = ({ label }) => {
  if (!label) return null;

  return (
    <div className="flex justify-center my-3">
      <span
        style={{
          background: "var(--chat-date-separator-bg)",
          color: "var(--chat-date-separator-text)",
          borderRadius: 9999,
          padding: "6px 12px",
          fontSize: 12,
          fontWeight: 700,
          boxShadow: "var(--chat-bubble-shadow)",
          border: "1px solid var(--chat-border-subtle)"
        }}
      >
        {label}
      </span>
    </div>
  );
};

const ServiceWorkerUpdateBanner = ({ onUpdate, onDismiss }) => {
  return (
    <div
      style={{
        position: "fixed",
        top: "calc(12px + env(safe-area-inset-top))",
        left: 12,
        right: 12,
        zIndex: 99999,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 680,
          background: "var(--surface-elevated)",
          border: "1px solid var(--border)",
          borderRadius: 18,
          boxShadow: "var(--shadow-floating)",
          padding: 14,
          pointerEvents: "auto"
        }}
      >
        <div className="flex items-start gap-3">
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 9999,
              background: "rgba(37, 211, 102, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            <RefreshCw className="w-5 h-5" style={{ color: "#25D366" }} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">
              È disponibile una nuova versione dell’app.
            </div>

            <div className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
              Aggiorna ora per usare subito l’ultima versione.
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={onUpdate}
                className="btn-primary flex items-center justify-center gap-2"
                style={{ minWidth: 150, padding: "10px 16px" }}
              >
                <RefreshCw className="w-4 h-4" />
                Aggiorna ora
              </button>

              <button
                type="button"
                onClick={onDismiss}
                style={{
                  height: 40,
                  padding: "0 14px",
                  borderRadius: 12,
                  border: "1px solid var(--border)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                  fontWeight: 700
                }}
              >
                Dopo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const ThemeToggleButton = ({ compact = true }) => {
  const [theme, setTheme] = useState(() => getInitialAppTheme());
  const isDark = theme === "dark";

  useEffect(() => {
    applyAppTheme(theme);

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (_) {}
  }, [theme]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === THEME_STORAGE_KEY) {
        setTheme(normalizeAppTheme(event.newValue));
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const toggleTheme = () => {
    setTheme((currentTheme) => currentTheme === "dark" ? "light" : "dark");
  };

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="inline-flex items-center justify-center"
      data-testid="theme-toggle"
      aria-label={isDark ? "Passa al tema giorno" : "Passa al tema notte"}
      title={isDark ? "Tema giorno" : "Tema notte"}
      style={{
        minWidth: compact ? 42 : 108,
        height: 42,
        padding: compact ? 0 : "0 14px",
        gap: 8,
        borderRadius: 9999,
        border: "1px solid var(--border)",
        background: "var(--surface-elevated)",
        color: "var(--text-primary)",
        boxShadow: "var(--shadow-floating)",
        fontWeight: 700
      }}
    >
      {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}

      {!compact && (
        <span className="text-sm">
          {isDark ? "Giorno" : "Notte"}
        </span>
      )}
    </button>
  );
};

// Auth Context
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me.php`);
      setUser(res.data);
    } catch (_) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login.php`, { email, password });
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    setUser(null);
    await clearAppBadgeCount();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, reloadUser: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Page
const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await login(form.email, form.password);
      navigate("/");
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Errore durante l'accesso"
      );
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center p-6" style={{ background: "var(--background)" }}>
      <div
        className="absolute right-4 z-10"
        data-testid="login-theme-toggle-wrapper"
        style={{ top: "calc(16px + env(safe-area-inset-top))" }}
      >
        <ThemeToggleButton compact={false} />
      </div>

      <div className="w-full max-w-sm animate-fade-in">
        <div className="flex justify-center mb-8">
          <img
            src="/logo-unico.png"
            alt="Logo Corona"
            className="w-24 h-24 object-contain"
          />
        </div>

        <h1 className="text-2xl font-bold text-center mb-2">Assistenza Clienti</h1>
        <p className="text-center mb-8" style={{ color: "var(--text-secondary)" }}>
          Accedi al tuo account
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input-field"
              style={{ paddingLeft: "48px" }}
              required
              data-testid="login-email"
            />
            <Mail
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
              style={{ color: "var(--text-muted)" }}
            />
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="input-field"
              style={{ paddingLeft: "48px", paddingRight: "48px" }}
              required
              data-testid="login-password"
            />
            <Lock
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
              style={{ color: "var(--text-muted)" }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2"
              style={{ color: "var(--text-muted)" }}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {error && (
            <p className="text-sm text-center" style={{ color: "#FF3B30" }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="login-submit"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Accedi"}
          </button>
        </form>

        <div
          className="mt-6 text-center text-xs"
          style={{ color: "var(--text-muted)" }}
        >
          Assistenza Clienti v{APP_VERSION}
        </div>
      </div>
    </div>
  );
};

// Category Icons
const CategoryIcon = ({ category, size = 24 }) => {
  const icons = {
    grafica: { icon: Megaphone, color: "var(--cat-grafica, #7c3aed)", bg: "rgba(124, 58, 237, 0.15)" },
    video: { icon: Video, color: "var(--cat-video)", bg: "rgba(255, 59, 48, 0.15)" },
    meta_ads: { icon: Megaphone, color: "var(--cat-ads)", bg: "rgba(175, 82, 222, 0.15)" },
    web_design: { icon: Globe, color: "var(--cat-web)", bg: "rgba(0, 122, 255, 0.15)" },
    commerciale: { icon: Briefcase, color: "var(--cat-commerciale, #f59e0b)", bg: "rgba(245, 158, 11, 0.15)" }
  };
  const config = icons[category] || icons.web_design;
  const Icon = config.icon;

  return (
    <div
      style={{
        width: size * 2,
        height: size * 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 9999,
        background: config.bg,
        flexShrink: 0
      }}
    >
      <Icon
        style={{
          color: config.color,
          width: size,
          height: size,
          display: "block",
          flexShrink: 0
        }}
      />
    </div>
  );
};

const categoryLabels = {
  grafica: "Grafica",
  video: "Videomaking",
  meta_ads: "Social e Meta Ads",
  web_design: "Programmazione e Web",
  commerciale: "Commerciale e Amministrazione"
};

const categoryDescriptions = {
  grafica: "Grafiche, materiali visual e creatività",
  video: "Riprese, reel, montaggi e contenuti video",
  meta_ads: "Campagne social, Meta Ads e sponsorizzate",
  web_design: "Siti web, landing page, e-commerce e sviluppo",
  commerciale: "Richieste commerciali e amministrative"
};

const statusLabels = {
  nuova: "Nuova",
  in_attesa: "In attesa",
  in_lavorazione: "In lavorazione",
  completata: "Completata"
};

const statusColors = {
  nuova: "var(--status-attesa)",
  in_attesa: "var(--status-attesa)",
  in_lavorazione: "var(--status-lavorazione)",
  completata: "var(--status-completata)"
};

const SmallTag = ({ children, icon = null, textColor = "#0b6bcb", borderColor = "#9fc5f8", bg = "#eaf3ff" }) => {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        height: 22,
        padding: "0 8px",
        borderRadius: 7,
        border: `1px solid ${borderColor}`,
        background: bg,
        color: textColor,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        lineHeight: 1,
        whiteSpace: "nowrap",
        flexShrink: 0
      }}
    >
      {icon}
      <span>{children}</span>
    </span>
  );
};

// Request Row
const RequestCard = ({ request, onClick, unreadCount = 0, archived = false }) => {
  const referenceDate = request.completata_at || request.updated_at || request.created_at;
  const date = new Date(referenceDate);
  const formattedTime = date.toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit"
  });

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left"
      data-testid={`request-card-${request.id}`}
      style={{ background: "transparent" }}
    >
      <div
        className="flex items-start gap-3 px-4 py-3"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <div
          className="shrink-0"
          style={{
            width: 44,
            height: 44,
            borderRadius: 9999,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0
          }}
        >
          <CategoryIcon category={request.categoria} size={16} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2 flex-wrap">
                <h3
                  className="font-semibold text-base truncate"
                  style={{ lineHeight: "20px", marginTop: 1 }}
                >
                  {request.titolo}
                </h3>

                <SmallTag
                  icon={
                    request.categoria === "grafica" ? <Megaphone className="w-3 h-3" /> :
                    request.categoria === "video" ? <Video className="w-3 h-3" /> :
                    request.categoria === "meta_ads" ? <Megaphone className="w-3 h-3" /> :
                    request.categoria === "web_design" ? <Globe className="w-3 h-3" /> :
                    request.categoria === "commerciale" ? <Briefcase className="w-3 h-3" /> :
                    <MessageCircle className="w-3 h-3" />
                  }
                >
                  {categoryLabels[request.categoria]}
                </SmallTag>

                {archived && (
                  <SmallTag
                    icon={<Archive className="w-3 h-3" />}
                    textColor="var(--archive-text)"
                    borderColor="var(--archive-border)"
                    bg="var(--archive-bg)"
                  >
                    Archivio
                  </SmallTag>
                )}
              </div>

              <div className="flex items-center gap-2 mt-2 min-w-0 flex-wrap">
                <span className="truncate text-sm" style={{ color: "var(--text-secondary)" }}>
                  {request.operatore_nome ? `Operatore: ${request.operatore_nome}` : "Non assegnato"}
                </span>

                <SmallTag
                  textColor={
                    request.stato === "in_attesa" || request.stato === "nuova"
                      ? "#a16207"
                      : request.stato === "in_lavorazione"
                        ? "#0b6bcb"
                        : "#15803d"
                  }
                  borderColor={
                    request.stato === "in_attesa" || request.stato === "nuova"
                      ? "#f4cf74"
                      : request.stato === "in_lavorazione"
                        ? "#9fc5f8"
                        : "#9ad9af"
                  }
                  bg={
                    request.stato === "in_attesa" || request.stato === "nuova"
                      ? "#fff7df"
                      : request.stato === "in_lavorazione"
                        ? "#eaf3ff"
                        : "#eaf9ee"
                  }
                >
                  {getChatStatusLabel(request.stato)}
                </SmallTag>
              </div>

              {archived && request.completata_at && (
                <div className="mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
                  Chiusa il {formatChatDateTime(request.completata_at)}
                </div>
              )}
            </div>

            <div className="shrink-0 flex flex-col items-end gap-2">
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                {formattedTime}
              </span>

              {!archived && unreadCount > 0 && (
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    minWidth: 22,
                    height: 22,
                    padding: "0 8px",
                    borderRadius: 9999,
                    background: "var(--primary)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: "22px"
                  }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

// New Request Modal
const NewRequestModal = ({ isOpen, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ categoria: "", titolo: "", messaggio: "" });

  const handleSubmit = async () => {
    if (!form.categoria || !form.titolo || !form.messaggio) return;

    setLoading(true);
    try {
      const res = await axios.post(`${API}/chats.php`, {
        categoria: form.categoria,
        titolo: form.titolo,
        messaggio_iniziale: form.messaggio
      });
      onCreated(res.data);
      setForm({ categoria: "", titolo: "", messaggio: "" });
      onClose();
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-4 p-4 border-b" style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} className="p-1" data-testid="close-modal">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h2 className="text-lg font-semibold">Nuova Richiesta</h2>
        </div>

        <div className="p-4 overflow-y-auto" style={{ maxHeight: "calc(90vh - 70px)" }}>
          <p className="text-sm font-medium mb-3" style={{ color: "var(--text-secondary)" }}>
            Seleziona categoria
          </p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {["grafica", "video", "meta_ads", "web_design", "commerciale"].map((cat) => (
              <div
                key={cat}
                className={`category-card ${form.categoria === cat ? "selected" : ""}`}
                onClick={() => setForm({ ...form, categoria: cat })}
                data-testid={`category-${cat}`}
                style={{
                  minHeight: 82,
                  padding: "8px",
                  justifyContent: "center",
                  alignItems: "stretch"
                }}
              >
                <div className="flex items-center justify-center gap-2 min-w-0">
                  <CategoryIcon category={cat} size={13} />
                  <p className="font-semibold text-[13px] leading-tight text-left min-w-0">
                    {categoryLabels[cat]}
                  </p>
                </div>

                <p
                  className="text-[10px] mt-1 leading-tight text-center"
                  style={{ color: "var(--text-muted)" }}
                >
                  {categoryDescriptions[cat]}
                </p>
              </div>
            ))}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Oggetto</label>
            <input
              type="text"
              placeholder="Breve descrizione della richiesta"
              value={form.titolo}
              onChange={(e) => setForm({ ...form, titolo: e.target.value })}
              className="input-field"
              data-testid="request-title"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Messaggio</label>
            <textarea
              placeholder="Descrivi in dettaglio la tua richiesta..."
              value={form.messaggio}
              onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
              className="input-field min-h-[120px] resize-none"
              data-testid="request-message"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading || !form.categoria || !form.titolo || !form.messaggio}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="submit-request"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                <Send className="w-5 h-5" />
                Invia Richiesta
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const LinkifiedText = ({ text }) => {
  const value = String(text || "");
  const regex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = value.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        regex.lastIndex = 0;

        if (regex.test(part)) {
          regex.lastIndex = 0;

          const href = part.toLowerCase().startsWith("www.")
            ? `https://${part}`
            : part;

          return (
            <a
              key={index}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }

        regex.lastIndex = 0;
        return <span key={index}>{part}</span>;
      })}
    </>
  );
};
const ChatBubble = ({ msg, isMine }) => {
  const bubbleBaseStyle = isMine
    ? {
        background: "var(--chat-bubble-sent)",
        color: "#ffffff",
        borderTopRightRadius: 8,
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)"
      }
    : {
        background: "var(--chat-bubble-received)",
        color: "var(--text-primary)",
        borderTopLeftRadius: 8,
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)"
      };

  const allegati = Array.isArray(msg.allegati) && msg.allegati.length > 0
    ? msg.allegati
    : (msg.allegato_url
      ? [{
          id: msg.id,
          url: msg.allegato_url,
          nome: msg.allegato_nome,
          tipo: msg.tipo
        }]
      : []);

  const hasOnlyAudio = allegati.length === 1 && allegati[0]?.tipo === "audio";

  const bubbleStyle = hasOnlyAudio
    ? {
        ...bubbleBaseStyle,
        width: "min(360px, calc(100vw - 48px))",
        maxWidth: "min(360px, calc(100vw - 48px))",
        padding: "8px 10px",
        borderRadius: 18
      }
    : {
        ...bubbleBaseStyle,
        maxWidth: "84%",
        padding: "8px 10px",
        borderRadius: 18
      };

  const timeLabel = new Date(msg.created_at).toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit"
  });

  const operatorName = msg.mittente_tipo === "operatore"
    ? String(msg.autore_nome || msg.operatore_nome || "").trim()
    : "";

  const metaLabel = operatorName
    ? `${timeLabel} · ${operatorName}`
    : timeLabel;

  const renderSingleAttachment = (attachment, index = 0) => {
    const attachmentUrl = getAttachmentUrl(attachment?.url);
    const attachmentTipo = attachment?.tipo;
    const attachmentNome = attachment?.nome;

    if (!attachmentUrl) return null;

    if (attachmentTipo === "immagine") {
      return (
        <a
          key={attachment?.id ?? index}
          href={attachmentUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src={attachmentUrl}
            alt={attachmentNome || `immagine-${index + 1}`}
            className="max-w-full rounded-2xl max-h-60 object-cover cursor-pointer"
            style={{ width: "100%" }}
          />
        </a>
      );
    }

    if (attachmentTipo === "video") {
      return (
        <div key={attachment?.id ?? index} className="overflow-hidden rounded-2xl">
          <video
            src={attachmentUrl}
            controls
            playsInline
            className="max-w-full max-h-72 block bg-black"
          />
        </div>
      );
    }

    if (attachmentTipo === "audio") {
      return (
        <div
          key={attachment?.id ?? index}
          style={{
            width: "100%",
            background: "var(--surface-elevated)",
            borderRadius: 14,
            padding: "8px",
            display: "block",
            boxShadow: "var(--chat-input-shadow)"
          }}
        >
          <audio
            key={`${attachment?.id ?? index}-${attachmentUrl}`}
            controls
            preload="auto"
            style={{
              width: "100%",
              height: 36,
              display: "block"
            }}
            src={attachmentUrl}
          />
        </div>
      );
    }

    return (
      <a
        key={attachment?.id ?? index}
        href={attachmentUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 underline"
      >
        <Paperclip className="w-4 h-4" />
        {attachmentNome}
      </a>
    );
  };

  const renderAttachments = () => {
    if (!allegati.length) return null;

    const soloImmagini = allegati.every((attachment) => attachment?.tipo === "immagine");

    if (soloImmagini && allegati.length > 1) {
      return (
        <div
          className="mb-2 grid gap-1"
          style={{
            gridTemplateColumns: allegati.length === 2 ? "repeat(2, 1fr)" : "repeat(2, minmax(0, 1fr))",
            maxWidth: 280
          }}
        >
          {allegati.map((attachment, index) => {
            const attachmentUrl = getAttachmentUrl(attachment?.url);
            return (
              <a
                key={attachment?.id ?? index}
                href={attachmentUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "block" }}
              >
                <img
                  src={attachmentUrl}
                  alt={attachment?.nome || `immagine-${index + 1}`}
                  className="w-full rounded-xl object-cover"
                  style={{ height: allegati.length === 2 ? 130 : 110 }}
                />
              </a>
            );
          })}
        </div>
      );
    }

    return (
      <div className={hasOnlyAudio ? "mb-1" : "mb-2"}>
        <div className="flex flex-col gap-2">
          {allegati.map((attachment, index) => renderSingleAttachment(attachment, index))}
        </div>
      </div>
    );
  };

  const showText = !(hasOnlyAudio && !msg.contenuto) && msg.contenuto && msg.contenuto.trim() !== "";

  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
      <div style={bubbleStyle}>
        {renderAttachments()}

        {showText && (
  <p
    className="text-sm leading-[1.35] whitespace-pre-wrap break-words"
    style={{ paddingRight: 42 }}
  >
    <LinkifiedText text={msg.contenuto} />
  </p>
)}

        <div
          className="text-[11px] flex justify-end mt-1"
          style={{ opacity: isMine ? 0.92 : 0.58 }}
        >
          {metaLabel}
        </div>
      </div>
    </div>
  );
};

// Chat View
const ChatView = ({ chatId, onBack, archived = false }) => {
  const { user } = useAuth();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordingError, setRecordingError] = useState("");

  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastMessageIdRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const currentRecordingMimeTypeRef = useRef("");
  const isStoppingRecordingRef = useRef(false);

  const chatsEndpoint = archived ? `${API}/chats_archive.php` : `${API}/chats.php`;
  const messagesEndpoint = archived ? `${API}/messages_archive.php` : `${API}/messages.php`;

  const scrollToBottom = useCallback((behavior = "smooth") => {
    requestAnimationFrame(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior
        });
      }
    });
  }, []);

  const stopMediaTracks = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
      });
      mediaStreamRef.current = null;
    }
  }, []);

  const stopRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const resetRecorderState = useCallback(() => {
    mediaRecorderRef.current = null;
    recordingChunksRef.current = [];
    currentRecordingMimeTypeRef.current = "";
    isStoppingRecordingRef.current = false;
  }, []);

  const getSupportedAudioMimeType = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg;codecs=opus",
      "audio/ogg"
    ];

    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      return "";
    }

    for (const mime of candidates) {
      try {
        if (window.MediaRecorder.isTypeSupported(mime)) {
          return mime;
        }
      } catch (_) {}
    }

    return "";
  };

  const getAudioExtensionByMimeType = (mimeType) => {
    const normalized = String(mimeType || "").toLowerCase();

    if (!normalized) return "webm";
    if (normalized.includes("webm")) return "webm";
    if (normalized.includes("ogg")) return "ogg";
    if (normalized.includes("mp4")) return "m4a";
    if (normalized.includes("mpeg")) return "mp3";
    if (normalized.includes("wav")) return "wav";
    return "webm";
  };

  const uploadVideoToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_VIDEO_UPLOAD_PRESET);
    formData.append("folder", `appassistenza/chat_${chatId}`);

    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/video/upload`,
      formData,
      {
        withCredentials: false
      }
    );

    if (!response?.data?.secure_url || !response?.data?.public_id) {
      throw new Error("Risposta Cloudinary non valida");
    }

    return {
      public_id: response.data.public_id,
      secure_url: response.data.secure_url
    };
  };

  const saveCloudinaryVideo = async (file, cloudinaryData) => {
    await axios.post(`${API}/save_cloudinary_video.php`, {
      user_id: user.id,
      chat_id: chatId,
      contenuto: file.name,
      nome_originale: file.name,
      cloudinary_public_id: cloudinaryData.public_id,
      cloudinary_url: cloudinaryData.secure_url,
      mime_type: file.type || "video/mp4",
      dimensione: file.size || 0
    });
  };

  const playNotificationSound = useCallback(async () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();
      }

      const ctx = audioContextRef.current;

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);

      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.23);
    } catch (_) {}
  }, []);

  const unlockAudio = useCallback(async () => {
    try {
      if (audioUnlockedRef.current) return;

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();
      }

      const ctx = audioContextRef.current;

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      audioUnlockedRef.current = true;
    } catch (_) {}
  }, []);

  const markChatAsRead = useCallback(async () => {
    if (archived || !user?.id || !chatId) return;

    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      return;
    }

    try {
      await axios.post(`${API}/read.php`, {
        chat_id: chatId,
        user_id: user.id,
        ruolo: user.ruolo
      });
    } catch (err) {
      console.error(err);
    }
  }, [archived, chatId, user]);

  const loadChat = useCallback(async () => {
    try {
      const chatRes = await axios.get(`${chatsEndpoint}?id=${chatId}`);
      setChat(chatRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [chatId, chatsEndpoint]);

  const loadMessages = useCallback(async (markRead = true) => {
    try {
      const messagesRes = await axios.get(`${messagesEndpoint}?chat_id=${chatId}`);
      const newMessages = Array.isArray(messagesRes.data) ? messagesRes.data : [];
      setMessages(newMessages);

      if (newMessages.length > 0) {
        const newestId = Math.max(...newMessages.map((m) => Number(m?.id ?? 0)));

        if (lastMessageIdRef.current === 0) {
          lastMessageIdRef.current = newestId;
        } else if (newestId > lastMessageIdRef.current) {
          const hasNewIncomingMessage = newMessages.some((m) => {
            const msgId = Number(m?.id ?? 0);
            const senderId = Number(m?.sender_id ?? 0);
            return msgId > lastMessageIdRef.current && senderId !== Number(user?.id ?? 0);
          });

          lastMessageIdRef.current = newestId;

          if (hasNewIncomingMessage && !archived) {
            playNotificationSound();
          }
        }
      }

      if (!archived && markRead) {
        await markChatAsRead();
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [chatId, user, playNotificationSound, messagesEndpoint, archived, markChatAsRead]);

  const loadData = useCallback(async (markRead = true) => {
    await Promise.all([
      loadChat(),
      loadMessages(markRead)
    ]);
  }, [loadChat, loadMessages]);

  useEffect(() => {
    const unlock = () => {
      unlockAudio();
    };

    window.addEventListener("touchstart", unlock, { passive: true });
    window.addEventListener("click", unlock, { passive: true });

    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };
  }, [unlockAudio]);

  useEffect(() => {
    lastMessageIdRef.current = 0;
    loadData(true);

    const interval = setInterval(() => {
      loadMessages(true);
    }, archived ? 15000 : 10000);

    return () => {
      clearInterval(interval);
      stopRecordingTimer();
      stopMediaTracks();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try {
          mediaRecorderRef.current.stop();
        } catch (_) {}
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (_) {}
        audioContextRef.current = null;
      }
      audioUnlockedRef.current = false;
      resetRecorderState();
    };
  }, [loadData, loadMessages, stopMediaTracks, stopRecordingTimer, resetRecorderState, archived]);

  useEffect(() => {
    if (!loading) {
      scrollToBottom(messages.length <= 1 ? "auto" : "smooth");
    }
  }, [messages, loading, scrollToBottom]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!archived && document.visibilityState === "visible") {
        await loadMessages(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [archived, loadMessages]);

  const sendMessage = async () => {
    if (archived || !newMessage.trim() || sending || recording) return;

    setSending(true);
    try {
      await axios.post(`${API}/messages.php?chat_id=${chatId}`, {
        contenuto: newMessage
      });
      setNewMessage("");
      await loadMessages(false);
    } catch (err) {
      console.error(err);
    }
    setSending(false);
  };

  const uploadAudioBlob = async (blob, mimeType) => {
    const finalMimeType = String(mimeType || blob?.type || "audio/webm").toLowerCase();
    const extension = getAudioExtensionByMimeType(finalMimeType);
    const fileName = `vocale_${new Date().toISOString().replace(/[:.]/g, "-")}.${extension}`;
    const file = new File([blob], fileName, {
      type: finalMimeType || "audio/webm"
    });

    if (!file || file.size <= 0) {
      throw new Error("File audio non valido");
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", user.id);
    formData.append("contenuto", "🎤 Vocale");
    formData.append("chat_id", chatId);

    await axios.post(`${API}/upload_attachment.php`, formData);
  };

  const startRecording = async () => {
    if (archived || sending || recording) return;

    setRecordingError("");

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setRecordingError("Il dispositivo non supporta la registrazione audio");
      return;
    }

    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      setRecordingError("Il browser non supporta i vocali");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      mediaStreamRef.current = stream;

      const mimeType = getSupportedAudioMimeType();
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recordingChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;
      currentRecordingMimeTypeRef.current = mimeType || mediaRecorder.mimeType || "";
      isStoppingRecordingRef.current = false;

      mediaRecorder.ondataavailable = (event) => {
        if (event?.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = () => {
        setRecordingError("Errore durante la registrazione del vocale");
        setRecording(false);
        stopRecordingTimer();
        stopMediaTracks();
        resetRecorderState();
      };

      mediaRecorder.onstop = async () => {
        stopRecordingTimer();
        setRecording(false);

        const chunks = [...recordingChunksRef.current];
        const finalMimeType =
          currentRecordingMimeTypeRef.current ||
          mediaRecorder.mimeType ||
          chunks?.[0]?.type ||
          "audio/webm";

        const audioBlob = new Blob(chunks, {
          type: finalMimeType
        });

        stopMediaTracks();
        resetRecorderState();

        if (!audioBlob || audioBlob.size <= 0) {
          setRecordingError("Registrazione non valida");
          return;
        }

        setSending(true);
        try {
          await uploadAudioBlob(audioBlob, finalMimeType);
          setRecordingSeconds(0);
          await loadMessages(false);
        } catch (err) {
          console.error(err);
          setRecordingError("Errore durante l'invio del vocale");
        }
        setSending(false);
      };

      mediaRecorder.start(250);
      setRecording(true);
      setRecordingSeconds(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error(err);
      setRecordingError("Microfono non disponibile o permesso negato");
      stopRecordingTimer();
      stopMediaTracks();
      resetRecorderState();
      setRecording(false);
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (!recorder || recorder.state === "inactive" || isStoppingRecordingRef.current) {
      return;
    }

    isStoppingRecordingRef.current = true;

    try {
      if (recorder.state === "recording") {
        try {
          recorder.requestData();
        } catch (_) {}

        setTimeout(() => {
          try {
            if (recorder.state !== "inactive") {
              recorder.stop();
            }
          } catch (err) {
            console.error(err);
            setRecording(false);
            stopRecordingTimer();
            stopMediaTracks();
            resetRecorderState();
            setRecordingError("Errore durante la chiusura della registrazione");
          }
        }, 150);
      }
    } catch (err) {
      console.error(err);
      setRecording(false);
      stopRecordingTimer();
      stopMediaTracks();
      resetRecorderState();
      setRecordingError("Errore durante la chiusura della registrazione");
    }
  };

  const toggleRecording = async () => {
    if (archived) return;

    if (recording) {
      stopRecording();
    } else {
      await startRecording();
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length || archived) return;

    setSending(true);

    try {
      const videoFiles = selectedFiles.filter(isVideoFile);
      const normalFiles = selectedFiles.filter((file) => !isVideoFile(file));

      if (videoFiles.length > 1) {
        throw new Error("Puoi inviare un solo video per volta");
      }

      if (videoFiles.length === 1 && normalFiles.length > 0) {
        throw new Error("Non puoi inviare insieme video e altri allegati");
      }

      if (videoFiles.length === 1) {
  const file = videoFiles[0];
  const MAX_VIDEO_SIZE = 100 * 1024 * 1024;

  if (file.size > MAX_VIDEO_SIZE) {
    throw new Error("Il video supera il limite massimo di 100 MB");
  }

  const cloudinaryData = await uploadVideoToCloudinary(file);
  await saveCloudinaryVideo(file, cloudinaryData);
} else {
  const formData = new FormData();

  normalFiles.forEach((file) => {
    formData.append("file[]", file);
  });

  formData.append("user_id", user.id);
  formData.append("contenuto", normalFiles.length > 1 ? "" : normalFiles[0].name);
  formData.append("chat_id", chatId);

  await axios.post(`${API}/upload_attachment.php`, formData);
}

      await loadMessages(false);
    } catch (err) {
      console.error(err);
      alert(
        err?.response?.data?.error ||
        err?.message ||
        "Errore durante il caricamento degli allegati"
      );
    }

    setSending(false);
    e.target.value = "";
  };

  const formatRecordingTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const currentStatusColor = getChatStatusColor(chat?.stato);

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--primary)" }} />
      </div>
    );
  }

  return (
    <div
      className="h-[100dvh] flex flex-col overflow-hidden"
      style={{
        backgroundColor: "var(--chat-bg)",
        backgroundImage:
          "var(--chat-pattern)",
        backgroundSize: "18px 18px"
      }}
    >
      <div
        className="shrink-0 z-10 border-b"
        style={{
          borderColor: "var(--chat-border-subtle)",
          background: "var(--chat-header)",
          paddingTop: "env(safe-area-inset-top)"
        }}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={onBack} className="p-1" data-testid="chat-back">
            <ArrowLeft className="w-6 h-6" />
          </button>

          <div
            className="shrink-0 flex items-center justify-center rounded-full"
            style={{
              width: 40,
              height: 40,
              background: "var(--chat-icon-bg)",
              color: "var(--chat-icon-text)"
            }}
          >
            <MessageCircle className="w-5 h-5" />
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="font-semibold truncate">{chat?.titolo}</h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: currentStatusColor }}
              />
              <span className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>
                {getChatStatusLabel(chat?.stato)}
              </span>

              {archived && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                  style={{
                    background: "var(--archive-bg)",
                    color: "var(--archive-text)",
                    border: "1px solid var(--archive-border)"
                  }}
                >
                  <Archive className="w-3 h-3" />
                  Sola lettura
                </span>
              )}
            </div>

            {archived && chat?.completata_at && (
              <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                Archiviata il {formatChatDateTime(chat.completata_at)}
              </div>
            )}
          </div>

          <div data-testid="chat-theme-toggle-wrapper">
            <ThemeToggleButton compact />
          </div>

          <button onClick={() => loadData(true)} className="p-2" data-testid="refresh-chat">
            <RefreshCw className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>
      </div>

      <div
        ref={messagesContainerRef}
        className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2"
      >
        {messages.map((msg, index) => {
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const currentDayKey = getMessageDayKey(msg.created_at);
          const previousDayKey = previousMessage ? getMessageDayKey(previousMessage.created_at) : "";
          const showDaySeparator = currentDayKey && currentDayKey !== previousDayKey;

          return (
            <div key={msg.id || `message-${index}`}>
              {showDaySeparator && (
                <ChatDateSeparator label={formatMessageDayLabel(msg.created_at)} />
              )}

              <ChatBubble
                msg={msg}
                isMine={Number(msg.sender_id) === Number(user?.id)}
              />
            </div>
          );
        })}

        {!loading && messages.length === 0 && (
          <div className="py-8 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
            Nessun messaggio disponibile.
          </div>
        )}
      </div>

      <div
        className="shrink-0 border-t"
        style={{
          borderColor: "var(--chat-border-subtle)",
          background: "var(--chat-header)",
          paddingBottom: `calc(10px + env(safe-area-inset-bottom))`
        }}
      >
        {archived ? (
          <div className="px-3 pt-3">
            <div
              className="rounded-[24px] px-4 py-4 text-sm"
              style={{
                background: "var(--chat-input)",
                color: "var(--text-secondary)",
                boxShadow: "var(--chat-input-shadow)"
              }}
            >
              Questa richiesta è archiviata. Puoi consultare messaggi e allegati, ma non puoi inviare nuove risposte.
            </div>
          </div>
        ) : (
          <>
            {recordingError && (
              <div className="px-4 pt-3 text-sm" style={{ color: "#FF3B30" }}>
                {recordingError}
              </div>
            )}

            {recording && (
              <div
                className="mx-3 mt-3 flex items-center justify-between rounded-2xl px-4 py-3"
                style={{
                  background: "rgba(255,59,48,0.08)",
                  border: "1px solid rgba(255,59,48,0.16)"
                }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-block w-3 h-3 rounded-full animate-pulse"
                    style={{ background: "#FF3B30" }}
                  />
                  <span className="text-sm font-medium">Registrazione in corso</span>
                </div>
                <span className="text-sm font-semibold">{formatRecordingTime(recordingSeconds)}</span>
              </div>
            )}

            <div className="px-3 pt-3">
              <div
                className="flex items-center gap-2 rounded-[28px] px-2 py-2"
                style={{
                  background: "var(--chat-header)"
                }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileUpload}
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime,video/webm,.pdf,audio/*,audio/webm,audio/ogg,audio/mp4,audio/x-m4a,audio/m4a,audio/mpeg,audio/aac,audio/x-aac,audio/wav,audio/x-wav,.m4a,.mp3,.ogg,.wav,.aac,.caf,.amr,.3gp,.3gpp"
                />

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="shrink-0 flex items-center justify-center rounded-full"
                  data-testid="attach-file"
                  disabled={sending || recording}
                  style={{
                    width: 42,
                    height: 42,
                    color: sending || recording ? "var(--text-muted)" : "var(--text-secondary)"
                  }}
                >
                  <Paperclip className="w-5 h-5" />
                </button>

                <button
                  onClick={toggleRecording}
                  disabled={sending}
                  className="shrink-0 flex items-center justify-center rounded-full"
                  data-testid="record-audio"
                  style={{
                    width: 42,
                    height: 42,
                    color: recording ? "var(--danger)" : "var(--text-secondary)"
                  }}
                >
                  {recording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>

                <div
                  className="flex-1 flex items-center rounded-[24px] px-4"
                  style={{
                    background: "var(--chat-input)",
                    minHeight: 48,
                    boxShadow: "var(--chat-input-shadow)"
                  }}
                >
                  <input
                    type="text"
                    placeholder={recording ? "Registrazione in corso..." : "Scrivi un messaggio"}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    className="w-full bg-transparent outline-none text-sm"
                    data-testid="chat-input"
                    disabled={recording}
                    style={{ color: "var(--text-primary)" }}
                  />
                </div>

                <button
                  onClick={sendMessage}
                  disabled={sending || !newMessage.trim() || recording}
                  className="shrink-0 flex items-center justify-center rounded-full"
                  style={{
                    width: 44,
                    height: 44,
                    background: sending || !newMessage.trim() || recording ? "var(--surface-lighter)" : "var(--primary)",
                    color: sending || !newMessage.trim() || recording ? "var(--text-secondary)" : "#ffffff",
                    transition: "all 0.2s ease"
                  }}
                  data-testid="send-message"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main App
const MainApp = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [archivedRequests, setArchivedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [operatoriOnline, setOperatoriOnline] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [requestingNotificationPermission, setRequestingNotificationPermission] = useState(false);
  const [activeTab, setActiveTab] = useState("attive");
  const syncingPushSubscriptionRef = useRef(false);

  const updateBadgeFromRequests = useCallback(async (items) => {
    const totalUnread = (Array.isArray(items) ? items : []).reduce((sum, item) => {
      return sum + Number(item?.non_letti_cliente ?? 0);
    }, 0);

    await setAppBadgeCount(totalUnread);
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const [activeRes, archiveRes] = await Promise.all([
        axios.get(`${API}/chats.php`),
        axios.get(`${API}/chats_archive.php`)
      ]);

      const activeData = Array.isArray(activeRes.data) ? activeRes.data : [];
      const archiveData = Array.isArray(archiveRes.data) ? archiveRes.data : [];

      setRequests(activeData);
      setArchivedRequests(archiveData);
      await updateBadgeFromRequests(activeData);
    } catch (err) {
      console.error(err);
      setRequests([]);
      setArchivedRequests([]);
      await clearAppBadgeCount();
    }
    setLoading(false);
  }, [updateBadgeFromRequests]);

  const loadOperatoriOnline = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/operatori_online.php`, {
        withCredentials: false
      });

      if (res.data?.ok) {
        setOperatoriOnline(Boolean(res.data.online));
      } else {
        setOperatoriOnline(null);
      }
    } catch (err) {
      setOperatoriOnline(null);
    }
  }, []);

  const syncPushSubscription = useCallback(async () => {
    if (!canUsePushNotifications() || syncingPushSubscriptionRef.current) {
      return;
    }

    if (Notification.permission !== "granted") {
      return;
    }

    syncingPushSubscriptionRef.current = true;

    try {
      await syncPushSubscriptionIfGranted();
    } catch (err) {
      console.error(err);
    } finally {
      syncingPushSubscriptionRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadRequests();
    loadOperatoriOnline();

    const requestsInterval = setInterval(loadRequests, 5000);
    const onlineInterval = setInterval(loadOperatoriOnline, 10000);

    return () => {
      clearInterval(requestsInterval);
      clearInterval(onlineInterval);
    };
  }, [loadRequests, loadOperatoriOnline]);

  useEffect(() => {
    if (user && notificationPermission === "granted") {
      syncPushSubscription();
    }
  }, [user, notificationPermission, syncPushSubscription]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        if (typeof Notification !== "undefined") {
          setNotificationPermission(Notification.permission);
        }
        await loadRequests();
        await loadOperatoriOnline();
        await syncPushSubscription();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadRequests, loadOperatoriOnline, syncPushSubscription]);

  const requestNotificationPermission = async () => {
    if (!canRequestNotifications() || requestingNotificationPermission) {
      return;
    }

    setRequestingNotificationPermission(true);

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission === "granted") {
        await syncPushSubscription();
        await loadRequests();
      }
    } catch (err) {
      console.error(err);
    }

    setRequestingNotificationPermission(false);
  };

  const handleRequestCreated = (newRequest) => {
    setRequests((prev) => [newRequest, ...prev]);
    setActiveTab("attive");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (selectedChat) {
    return (
      <ChatView
        chatId={selectedChat.id}
        archived={Boolean(selectedChat.archived)}
        onBack={() => {
          setSelectedChat(null);
          loadRequests();
          loadOperatoriOnline();
          window.scrollTo({ top: 0, behavior: "auto" });
        }}
      />
    );
  }

  const showNotificationButton =
    canRequestNotifications() &&
    notificationPermission !== "granted";

  const showIosInstallHint =
    isIosDevice() &&
    !isStandalonePwa() &&
    canUsePushNotifications();

  const currentRequests = activeTab === "archivio" ? archivedRequests : requests;

  return (
    <div className="min-h-[100dvh] flex flex-col" style={{ background: "var(--background)" }}>
      <div className="p-4 pt-6" style={{ background: "var(--background)" }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Ciao, {user?.nome}</h1>
            <p style={{ color: "var(--text-secondary)" }}>Le tue richieste</p>

            <div
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
            >
              <Users className="w-4 h-4" style={{ color: "var(--primary)" }} />
              <span className="text-sm font-medium">Operatori online</span>
              <span
                className="inline-block w-3 h-3 rounded-full"
                style={{
                  background: operatoriOnline === null
                    ? "var(--text-muted)"
                    : operatoriOnline
                      ? "#34C759"
                      : "#FF3B30"
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2" data-testid="main-theme-toggle-wrapper">
            <ThemeToggleButton compact />

            <button
              onClick={handleLogout}
              className="p-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-6 h-6" style={{ color: "var(--danger)" }} />
            </button>
          </div>
        </div>

        {showIosInstallHint && (
          <div
            className="mt-4 rounded-2xl p-4"
            style={{
              background: "var(--surface-elevated)",
              border: "1px solid var(--border)"
            }}
          >
            <div className="flex items-start gap-3">
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 9999,
                  background: "rgba(37, 211, 102, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}
              >
                <Bell className="w-5 h-5" style={{ color: "var(--primary)" }} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">Installa la web app su iPhone</div>
                <div className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
                  Per ricevere notifiche push e badge su iPhone devi aprire il sito in Safari e usare “Aggiungi a Home”.
                </div>
              </div>
            </div>
          </div>
        )}

        {showNotificationButton && !showIosInstallHint && (
          <div
            className="mt-4 rounded-2xl p-4"
            style={{
              background: "var(--surface-elevated)",
              border: "1px solid var(--border)"
            }}
          >
            <div className="flex items-start gap-3">
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 9999,
                  background: "rgba(37, 211, 102, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}
              >
                <Bell className="w-5 h-5" style={{ color: "var(--primary)" }} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">Abilita notifiche</div>
                <div className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
                  Serve per ricevere notifiche push e aggiornare correttamente il badge.
                </div>

                <button
                  type="button"
                  onClick={requestNotificationPermission}
                  disabled={requestingNotificationPermission}
                  className="btn-primary mt-3 flex items-center justify-center gap-2"
                  style={{ minWidth: 180, padding: "12px 18px" }}
                >
                  {requestingNotificationPermission ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                  Abilita notifiche
                </button>

                {notificationPermission === "denied" && (
                  <div className="mt-2 text-sm" style={{ color: "#FF3B30" }}>
                    Hai negato il permesso. Riabilitalo dalle impostazioni del browser o della web app.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4">
          <div
            className="inline-flex rounded-2xl p-1"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              width: "100%"
            }}
          >
            <button
              type="button"
              onClick={() => setActiveTab("attive")}
              data-testid="tab-attive"
              style={{
                flex: 1,
                height: 44,
                borderRadius: 14,
                fontSize: 14,
                fontWeight: 700,
                background: activeTab === "attive" ? "var(--tab-active-bg)" : "transparent",
                color: activeTab === "attive" ? "var(--tab-active-text)" : "var(--text-secondary)",
                boxShadow: activeTab === "attive" ? "0 1px 2px rgba(0,0,0,0.06)" : "none"
              }}
            >
              Attive {requests.length > 0 ? `(${requests.length})` : ""}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("archivio")}
              data-testid="tab-archivio"
              style={{
                flex: 1,
                height: 44,
                borderRadius: 14,
                fontSize: 14,
                fontWeight: 700,
                background: activeTab === "archivio" ? "var(--tab-active-bg)" : "transparent",
                color: activeTab === "archivio" ? "var(--tab-active-text)" : "var(--text-secondary)",
                boxShadow: activeTab === "archivio" ? "0 1px 2px rgba(0,0,0,0.06)" : "none"
              }}
            >
              Archivio {archivedRequests.length > 0 ? `(${archivedRequests.length})` : ""}
            </button>
          </div>
        </div>
      </div>

      <div className="pb-28">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--primary)" }} />
          </div>
        ) : currentRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            {activeTab === "archivio" ? (
              <Archive className="w-16 h-16 mb-4" style={{ color: "var(--text-muted)" }} />
            ) : (
              <MessageCircle className="w-16 h-16 mb-4" style={{ color: "var(--text-muted)" }} />
            )}

            <h3 className="text-xl font-semibold mb-2">
              {activeTab === "archivio" ? "Nessuna richiesta archiviata" : "Nessuna richiesta"}
            </h3>

            <p style={{ color: "var(--text-secondary)" }}>
              {activeTab === "archivio"
                ? "Qui troverai tutte le richieste concluse e archiviate."
                : "Non hai ancora inviato richieste di assistenza. Tocca il pulsante + per iniziare."}
            </p>
          </div>
        ) : (
          <div style={{ background: "var(--background)" }}>
            <div
              style={{
                background: "var(--surface)",
                borderTop: "1px solid var(--border)",
                borderBottom: "1px solid var(--border)"
              }}
            >
              {currentRequests.map((request) => {
                const unreadCount = Number(request.non_letti_cliente ?? 0);
                const isArchivedTab = activeTab === "archivio";

                return (
                  <RequestCard
                    key={`${isArchivedTab ? "arch" : "act"}-${request.id}`}
                    request={request}
                    unreadCount={unreadCount}
                    archived={isArchivedTab}
                    onClick={() => setSelectedChat({ id: request.id, archived: isArchivedTab })}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {user?.ruolo === "cliente" && activeTab === "attive" && (
        <button
          className="fab"
          onClick={() => setShowNewRequest(true)}
          data-testid="new-request-fab"
          style={{ bottom: "calc(12px + env(safe-area-inset-bottom))" }}
        >
          <Plus className="w-7 h-7" />
        </button>
      )}

      <NewRequestModal
        isOpen={showNewRequest}
        onClose={() => setShowNewRequest(false)}
        onCreated={handleRequestCreated}
      />
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--background)" }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--primary)" }} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppShell() {
  const { updateAvailable, applyUpdate, dismissUpdate } = useServiceWorkerUpdate();

  return (
    <BrowserRouter>
      <div className="min-h-screen w-screen" style={{ background: "var(--background)" }}>
        {updateAvailable && (
          <ServiceWorkerUpdateBanner
            onUpdate={applyUpdate}
            onDismiss={dismissUpdate}
          />
        )}

        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;